import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { Drawer, List, Row, Space, Typography } from 'antd';
import config from 'config';
import {
  LocationOutput,
  useGetLocationsLazyQuery,
  useGetLocationsQuery,
} from 'gql/types';
import { useDebounce } from 'modules/common/hooks';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

import Button from '../Button';
import Checkbox from '../Checkbox';
import Input from '../Input';
import Spin from '../Spin';

type LocationsPickerDrawerProps = {
  visible: boolean;
  onClose: () => void;
  selectedLocations: string[];
  onApply: (locations: string[]) => void;
};

export const LocationsPickerDrawer: FC<LocationsPickerDrawerProps> = ({
  visible,
  onClose,
  selectedLocations,
  onApply,
}) => {
  const { t } = useTranslation('common.LocationsPickerDrawer');

  const [localSelected, setLocalSelected] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedLocations, setAllLoadedLocations] = useState<
    LocationOutput[]
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const [isSearchPending, setIsSearchPending] = useState(false);

  const sortedLocations = useMemo(() => {
    return [...allLoadedLocations].sort((a, b) => a.name.localeCompare(b.name));
  }, [allLoadedLocations]);

  useEffect(() => {
    if (visible) {
      setLocalSelected(selectedLocations);
      setSearchValue('');
      setCurrentPage(1);
    }
  }, [visible, selectedLocations]);

  const { data: _initialData, loading: initialLoading } = useGetLocationsQuery({
    variables: {
      input: {
        take: config.PAGINATION.LOCATIONS_PAGE_SIZE,
        skip: 0,
      },
    },
    skip: !visible,
    onCompleted: (data) => {
      const locations = data.getLocations.data || [];
      setAllLoadedLocations(locations);
      setHasMore(locations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
    },
  });

  const [fetchLocations, { loading: searchLoading }] =
    useGetLocationsLazyQuery();

  const isSearching = isSearchPending || searchLoading;
  const isInitialLoading = initialLoading && allLoadedLocations.length === 0;

  const { debounce } = useDebounce(300);

  const performSearch = useCallback(
    async (query: string) => {
      setCurrentPage(1);

      try {
        const { data } = await fetchLocations({
          variables: {
            input: {
              take: config.PAGINATION.LOCATIONS_PAGE_SIZE,
              skip: 0,
              filter: {
                searchingQuery: query || undefined,
              },
            },
          },
        });

        const locations = data?.getLocations.data || [];
        setAllLoadedLocations(locations);
        setHasMore(locations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
      } finally {
        setIsSearchPending(false);
      }
    },
    [fetchLocations]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setIsSearchPending(true);
    debounce(performSearch)(value);
  };

  const handleLoadMore = async () => {
    const nextPage = currentPage + 1;
    const skip = (nextPage - 1) * config.PAGINATION.LOCATIONS_PAGE_SIZE;

    const { data } = await fetchLocations({
      variables: {
        input: {
          take: config.PAGINATION.LOCATIONS_PAGE_SIZE,
          skip,
          filter: {
            searchingQuery: searchValue || undefined,
          },
        },
      },
    });

    const newLocations = data?.getLocations.data || [];

    setAllLoadedLocations((prev) => {
      const existingIds = new Set(prev.map((loc) => loc.id));
      const uniqueNewLocations = newLocations.filter(
        (loc) => !existingIds.has(loc.id)
      );
      return [...prev, ...uniqueNewLocations];
    });

    setCurrentPage(nextPage);
    setHasMore(newLocations.length === config.PAGINATION.LOCATIONS_PAGE_SIZE);
  };

  const handleCheckboxChange = (locationName: string, checked: boolean) => {
    if (checked) {
      setLocalSelected((prev) => [...prev, locationName]);
    } else {
      setLocalSelected((prev) => prev.filter((name) => name !== locationName));
    }
  };

  const handleSelectAll = () => {
    const allNames = sortedLocations.map((loc) => loc.name);
    setLocalSelected(allNames);
  };

  const handleClearAll = () => {
    setLocalSelected([]);
  };

  const handleApply = () => {
    onApply(localSelected);
    onClose();
  };

  const handleBack = () => {
    onClose();
  };

  return (
    <Drawer
      title={
        <Row align="middle" style={{ gap: 12 }}>
          <ArrowLeftOutlined onClick={handleBack} className={styles.backIcon} />
          <span>{t('selectLocations')}</span>
        </Row>
      }
      placement="right"
      width="100%"
      open={visible}
      onClose={handleBack}
      closable={false}
      className={styles.drawer}
      footer={
        <Row justify="space-between" style={{ gap: 16 }}>
          <Button size="large" onClick={handleClearAll} style={{ flex: 1 }}>
            {t('clearAll')}
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={handleApply}
            style={{ flex: 1 }}
          >
            {t('done')}{' '}
            {localSelected.length > 0 && `(${localSelected.length})`}
          </Button>
        </Row>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div className={styles.searchContainer}>
          <Input
            placeholder={t('searchLocations')}
            value={searchValue}
            onChange={handleSearchChange}
            prefix={<SearchOutlined />}
            suffix={isSearching ? <Spin size="small" /> : null}
            allowClear
            size="large"
          />
        </div>

        {sortedLocations.length > 0 && (
          <Row justify="space-between" className={styles.quickActions}>
            <Typography.Text type="secondary">
              {localSelected.length} {t('selected')}
            </Typography.Text>
            <Button type="link" size="small" onClick={handleSelectAll}>
              {t('selectAll')}
            </Button>
          </Row>
        )}

        <div className={styles.listContainer}>
          {isInitialLoading ? (
            <Row
              justify="center"
              align="middle"
              className={styles.spinnerContainer}
            >
              <Spin size="large" />
            </Row>
          ) : (
            <Spin spinning={isSearching} size="small">
              {sortedLocations.length === 0 ? (
                <Row
                  justify="center"
                  align="middle"
                  className={styles.emptyState}
                >
                  <Typography.Text type="secondary">
                    {t('noLocationsFound')}
                  </Typography.Text>
                </Row>
              ) : (
                <List
                  dataSource={sortedLocations}
                  renderItem={(location) => (
                    <List.Item className={styles.listItem}>
                      <Checkbox
                        checked={localSelected.includes(location.name)}
                        onChange={(e) =>
                          handleCheckboxChange(location.name, e.target.checked)
                        }
                      >
                        {location.name}
                      </Checkbox>
                    </List.Item>
                  )}
                  loadMore={
                    hasMore && (
                      <Row
                        justify="center"
                        className={styles.loadMoreContainer}
                      >
                        <Button
                          type="link"
                          onClick={handleLoadMore}
                          loading={searchLoading}
                        >
                          {t('loadMore')}
                        </Button>
                      </Row>
                    )
                  }
                />
              )}
            </Spin>
          )}
        </div>
      </Space>
    </Drawer>
  );
};
